<script setup lang="ts">
import { Gauge, KeyRound, LogOut, Route, ScanLine, SendHorizontal, ShieldAlert, ShieldCheck } from '@lucide/vue'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'

import { PBadge, PButton, PInput, PToast, useToast } from '@/design-system'

import {
    AuthError,
    complete,
    getSession,
    inspect,
    logout as apiLogout,
    type CompleteResult,
    type InspectData,
    type PrellmHeaders,
    type SessionState,
    type Tier,
} from './api'
import { catalogFor, type CatalogKind } from './catalog'
import LoginView from './components/LoginView.vue'
import PipelineStage from './components/PipelineStage.vue'
import ScenarioDialog from './components/ScenarioDialog.vue'
import { segment } from './highlight'
import HighlightedText from './HighlightedText.vue'

/**
 * Démo PreLLM — « poste de contrôle ». Accès gardé par un login (email/mot de
 * passe contre le control-plane). À gauche, l'application telle que la voit
 * l'utilisateur ; à droite, ce que PreLLM exécute en coulisses, étape par
 * étape. Les URLs des plans sont fixées côté serveur (env) ; seule la clé
 * pk_live est saisie dans l'app, changeable à la volée pendant la démo.
 */

const toast = useToast()

// --- Auth ---------------------------------------------------------------
const session = ref<SessionState | null>(null)
const booting = ref(true)
const authed = computed(() => session.value?.authenticated === true)
const host = computed(() => session.value?.dataPlaneHost || '—')
const userEmail = computed(() => session.value?.user?.email || '')

onMounted(async () => {
    session.value = await getSession()
    booting.value = false
})

function onAuthenticated(state: SessionState): void {
    session.value = state
}

async function doLogout(): Promise<void> {
    await apiLogout()
    turns.value = []
    resetRun()
    session.value = { authenticated: false, user: null, dataPlaneHost: session.value?.dataPlaneHost || '' }
}

// --- Configuration (persistée) -----------------------------------------
const apiKey = ref(localStorage.getItem('prellm.demo.apiKey') || '')
const tier = ref<Tier>((localStorage.getItem('prellm.demo.tier') as Tier) || 'light')

watch(apiKey, (v) => localStorage.setItem('prellm.demo.apiKey', v))
watch(tier, (v) => localStorage.setItem('prellm.demo.tier', v))

// --- Conversation -------------------------------------------------------
type TurnKind = 'answer' | 'blocked' | 'error'
interface ChatTurn {
    id: string
    role: 'user' | 'prellm'
    kind?: TurnKind
    text: string
    meta?: PrellmHeaders
}

const turns = ref<ChatTurn[]>([])
const draft = ref('')
const sending = ref(false)
const messagesEl = ref<HTMLElement | null>(null)

// --- État du pipeline (un « run » par envoi) ---------------------------
type StageStatus = 'idle' | 'running' | 'done' | 'blocked' | 'skipped' | 'error'
interface RunState {
    started: boolean
    inspect: InspectData | null
    result: CompleteResult | null
    stages: {
        inspection: StageStatus
        injection: StageStatus
        routing: StageStatus
        cost: StageStatus
    }
}
const run = reactive<RunState>({
    started: false,
    inspect: null,
    result: null,
    stages: { inspection: 'idle', injection: 'idle', routing: 'idle', cost: 'idle' },
})
function resetRun(): void {
    run.started = false
    run.inspect = null
    run.result = null
    run.stages.inspection = 'idle'
    run.stages.injection = 'idle'
    run.stages.routing = 'idle'
    run.stages.cost = 'idle'
}
const hasRun = computed(() => run.started)

// --- Libellés PII -------------------------------------------------------
const PII_LABELS: Record<string, string> = {
    email: 'Email',
    iban: 'IBAN',
    credit_card: 'Carte bancaire',
    api_key: 'Clé API',
    jwt: 'Jeton JWT',
    db_credential: 'Identifiants BDD',
    nir: 'N° sécu (NIR)',
    siret: 'SIRET',
    siren: 'SIREN',
    phone_fr: 'Téléphone',
    ssn: 'SSN (US)',
    person: 'Personne',
    address: 'Adresse',
    private_key: 'Clé privée',
    url_credential: 'URL avec secret',
    generic_secret: 'Secret',
}
function piiLabel(type: string): string {
    return PII_LABELS[type] ?? type
}

// --- Vue gouvernance (riche, /demo/inspect super_admin) ----------------
const piiCount = computed(() => run.inspect?.pii.count ?? 0)
const injectionScorePct = computed(() =>
    run.inspect ? Math.round(run.inspect.injection.score * 100) : 0,
)
const beforeSegments = computed(() =>
    run.inspect
        ? segment(run.inspect.input, run.inspect.pii.detections.map((d) => d.original))
        : [],
)
const afterSegments = computed(() =>
    run.inspect
        ? segment(run.inspect.sanitized, run.inspect.pii.detections.map((d) => d.token))
        : [],
)
const routing = computed(() => (run.result?.ok ? run.result.headers : null))

function cacheHitPct(h: PrellmHeaders): number | null {
    if (!h.cacheHitRatio) return null
    return Math.round(Number(h.cacheHitRatio) * 100)
}

// --- Catalogue (dialog secrets / injections) ---------------------------
const dialogOpen = ref(false)
const dialogKind = ref<CatalogKind>('secrets')
function openDialog(kind: CatalogKind): void {
    dialogKind.value = kind
    dialogOpen.value = true
}
function onSelectPrompt(p: { text: string; tier?: Tier }): void {
    draft.value = p.text
    if (p.tier) tier.value = p.tier
}

// --- Helpers ------------------------------------------------------------
function pushTurn(turn: Omit<ChatTurn, 'id'>): void {
    turns.value.push({ id: crypto.randomUUID(), ...turn })
    void scrollToBottom()
}
async function scrollToBottom(): Promise<void> {
    await nextTick()
    if (messagesEl.value) messagesEl.value.scrollTop = messagesEl.value.scrollHeight
}
function onComposerKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        void send()
    }
}
const tick = (ms: number) => new Promise((r) => setTimeout(r, ms))

function metaLine(h?: PrellmHeaders): string {
    if (!h) return ''
    const parts: string[] = []
    if (h.provider) parts.push(h.provider)
    if (h.model) parts.push(h.model)
    if (h.latencyMs) parts.push(`${h.latencyMs} ms`)
    return parts.join(' · ')
}

function handleAuthError(): void {
    toast.error('Session expirée — reconnecte-toi.')
    session.value = { authenticated: false, user: null, dataPlaneHost: session.value?.dataPlaneHost || '' }
}

// --- Orchestration ------------------------------------------------------
async function send(): Promise<void> {
    const text = draft.value.trim()
    if (!text || sending.value) return
    if (!apiKey.value.trim()) {
        toast.error('Renseigne ta clé pk_live.')
        return
    }

    sending.value = true
    pushTurn({ role: 'user', text })
    draft.value = ''
    resetRun()

    try {
        run.started = true
        // Étape 1 — inspection gouvernance via /demo/inspect (réservé
        // super_admin côté data-plane). C'est la source du poste de contrôle :
        // avant/après tokenisé + score d'injection, sans appeler de LLM.
        run.stages.inspection = 'running'
        const ins = await inspect(apiKey.value, text)
        await tick(220)

        if (!ins.available) {
            // Pas de dégradation : sans /demo/inspect, pas de poste de
            // contrôle. La clé doit porter le rôle super_admin.
            run.stages.inspection = 'error'
            run.stages.injection = 'skipped'
            run.stages.routing = 'skipped'
            run.stages.cost = 'skipped'
            pushTurn({
                role: 'prellm',
                kind: 'error',
                text: 'Inspection indisponible : cette clé n’a pas le rôle super_admin requis par le poste de contrôle PreLLM.',
            })
            toast.error('Clé non super_admin — poste de contrôle indisponible')
            return
        }

        run.inspect = ins
        run.stages.inspection = 'done'
        if (ins.pii.count > 0) {
            toast.success(
                `${ins.pii.count} donnée(s) sensible(s) neutralisée(s) : ${ins.pii.types
                    .map(piiLabel)
                    .join(', ')}`,
            )
        }

        run.stages.injection = 'running'
        await tick(260)
        if (ins.injection.detected) {
            run.stages.injection = 'blocked'
            run.stages.routing = 'skipped'
            run.stages.cost = 'skipped'
            pushTurn({
                role: 'prellm',
                kind: 'blocked',
                text: 'Requête bloquée : tentative de manipulation détectée. Le message n’a jamais été transmis au fournisseur.',
            })
            toast.error(`Injection bloquée — score ${injectionScorePct.value} %`)
            // /demo/inspect n'écrit aucun audit. On envoie donc quand même
            // la requête au data-plane (/v1/messages) : il la bloque (403)
            // ET journalise l'event d'injection dans le control-plane.
            void complete(apiKey.value, text, tier.value).catch(() => {})
            return
        }
        run.stages.injection = 'done'

        // Étapes 3 & 4 — vrai appel LLM via le data-plane.
        run.stages.routing = 'running'
        const r = await complete(apiKey.value, text, tier.value)
        run.result = r
        await tick(220)

        if (r.blocked) {
            run.stages.injection = 'blocked'
            run.stages.routing = 'skipped'
            run.stages.cost = 'skipped'
            pushTurn({
                role: 'prellm',
                kind: 'blocked',
                text: 'Requête bloquée par la politique de contenu (403). Jamais transmise au fournisseur.',
            })
            toast.error('Bloqué par PreLLM (403)')
            return
        }
        if (!r.ok) {
            run.stages.routing = 'error'
            run.stages.cost = 'skipped'
            pushTurn({ role: 'prellm', kind: 'error', text: r.errorMessage || `Erreur ${r.status}` })
            toast.error(`Appel LLM : ${r.errorMessage || r.status}`)
            return
        }

        run.stages.routing = 'done'
        run.stages.cost = 'running'
        await tick(220)
        run.stages.cost = 'done'

        pushTurn({ role: 'prellm', kind: 'answer', text: r.answer, meta: r.headers })
        const meta = metaLine(r.headers)
        toast.success(`Réponse réelle${meta ? ' · ' + meta : ''}`)
        if (r.headers.fallbackTriggered === 'true') {
            toast.warning('Fallback déclenché — le provider de secours a répondu')
        }
    } catch (err) {
        if (err instanceof AuthError) {
            handleAuthError()
            return
        }
        const message = err instanceof Error ? err.message : 'erreur réseau'
        if (run.stages.inspection === 'running') run.stages.inspection = 'error'
        if (run.stages.routing === 'running') run.stages.routing = 'error'
        pushTurn({
            role: 'prellm',
            kind: 'error',
            text: `Échec de l’appel : ${message}.`,
        })
        toast.error(`Impossible de joindre PreLLM : ${message}`)
    } finally {
        sending.value = false
        void scrollToBottom()
    }
}
</script>

<template>
    <!-- Boot : on attend l'état de session avant de décider login / démo -->
    <div v-if="booting" class="boot"></div>

    <!-- Non connecté → écran de login -->
    <LoginView v-else-if="!authed" @authenticated="onAuthenticated" />

    <!-- Connecté → la démo -->
    <div v-else class="demo">
        <header class="demo__topbar">
            <div class="demo__bar">
                <div class="demo__brand">
                    <span class="demo__logo">PreLLM</span>
                    <span class="demo__tagline">
                        Gouvernance LLM, en direct
                        <span class="demo__host">· {{ host }}</span>
                    </span>
                </div>
                <div class="demo__config">
                    <PInput
                        v-model="apiKey"
                        label="Clé pk_live"
                        type="password"
                        size="sm"
                        placeholder="pk_live_…"
                    />
                    <label class="demo__field">
                        <span class="demo__field-label">Tier</span>
                        <select v-model="tier" class="p-select demo__select">
                            <option value="light">light</option>
                            <option value="medium">medium</option>
                            <option value="heavy">heavy</option>
                        </select>
                    </label>
                    <div class="demo__user">
                        <span v-if="userEmail" class="demo__email">{{ userEmail }}</span>
                        <button type="button" class="demo__logout" title="Se déconnecter" @click="doLogout">
                            <LogOut :size="15" :stroke-width="2" />
                        </button>
                    </div>
                </div>
            </div>
        </header>

        <main class="demo__main">
            <!-- Colonne gauche : application cliente -->
            <section class="chat">
                <div class="chat__head">
                    <span class="chat__eyebrow">Votre application</span>
                    <span class="chat__host">{{ host }}</span>
                </div>

                <div ref="messagesEl" class="chat__messages">
                    <div v-if="turns.length === 0" class="chat__empty">
                        <p class="chat__empty-title">Tapez une donnée sensible. Regardez PreLLM l’intercepter.</p>
                        <p class="chat__empty-sub">
                            Chaque message est inspecté, classé, acheminé et facturé — en direct,
                            avant qu’il n’atteigne le fournisseur. Choisissez un scénario ou écrivez
                            le vôtre.
                        </p>
                    </div>

                    <div
                        v-for="turn in turns"
                        :key="turn.id"
                        :class="['bubble', `bubble--${turn.role}`, turn.kind ? `bubble--${turn.kind}` : '']"
                    >
                        <template v-if="turn.role === 'user'">
                            <span class="bubble__who">Utilisateur</span>
                            <p class="bubble__text">{{ turn.text }}</p>
                        </template>
                        <template v-else-if="turn.kind === 'blocked'">
                            <span class="bubble__who">PreLLM · bloqué</span>
                            <p class="bubble__text">{{ turn.text }}</p>
                        </template>
                        <template v-else-if="turn.kind === 'error'">
                            <span class="bubble__who">PreLLM · erreur</span>
                            <p class="bubble__text">{{ turn.text }}</p>
                        </template>
                        <template v-else>
                            <span class="bubble__who">Assistant</span>
                            <p class="bubble__text">{{ turn.text }}</p>
                            <p v-if="metaLine(turn.meta)" class="bubble__meta">{{ metaLine(turn.meta) }}</p>
                        </template>
                    </div>
                </div>

                <div class="actions">
                    <button type="button" class="action action--secrets" @click="openDialog('secrets')">
                        <KeyRound :size="16" :stroke-width="2" />
                        <span class="action__label">Secrets &amp; PII</span>
                        <span class="action__sub">emails, IBAN, clés API…</span>
                    </button>
                    <button type="button" class="action action--injection" @click="openDialog('injection')">
                        <ShieldAlert :size="16" :stroke-width="2" />
                        <span class="action__label">Injections de prompt</span>
                        <span class="action__sub">jailbreak, override…</span>
                    </button>
                </div>

                <div class="composer">
                    <textarea
                        v-model="draft"
                        class="composer__input"
                        rows="3"
                        placeholder="Écrivez un message… (Entrée pour envoyer, Maj+Entrée = saut de ligne)"
                        @keydown="onComposerKeydown"
                    ></textarea>
                    <PButton variant="primary" :loading="sending" @click="send">
                        <template #icon><SendHorizontal :size="16" /></template>
                        Envoyer
                    </PButton>
                </div>
            </section>

            <!-- Colonne droite : poste de contrôle -->
            <aside class="control">
                <div class="control__head">
                    <span class="chat__eyebrow">Poste de contrôle PreLLM</span>
                </div>

                <div class="pipeline">
                    <PipelineStage
                        :index="1"
                        title="Inspection des données"
                        caption="Détecte et tokenise les données sensibles avant tout envoi."
                        :icon="ScanLine"
                        :status="run.stages.inspection"
                    >
                        <template v-if="run.inspect">
                            <div v-if="piiCount > 0" class="diff">
                                <div class="diff__col">
                                    <span class="diff__label">Ce que l’utilisateur a tapé</span>
                                    <div class="diff__text">
                                        <HighlightedText :segments="beforeSegments" variant="leak" />
                                    </div>
                                </div>
                                <div class="diff__col">
                                    <span class="diff__label">Ce qui part chez le fournisseur</span>
                                    <div class="diff__text">
                                        <HighlightedText :segments="afterSegments" variant="token" />
                                    </div>
                                </div>
                                <div class="detections">
                                    <div
                                        v-for="(d, i) in run.inspect.pii.detections"
                                        :key="i"
                                        class="detection"
                                    >
                                        <PBadge variant="brand" size="sm">{{ piiLabel(d.type) }}</PBadge>
                                        <code class="detection__orig">{{ d.original }}</code>
                                        <span class="detection__arrow">→</span>
                                        <code class="detection__token">{{ d.token }}</code>
                                    </div>
                                </div>
                            </div>
                            <p v-else class="muted">Aucune donnée sensible détectée dans ce message.</p>
                        </template>
                    </PipelineStage>

                    <PipelineStage
                        :index="2"
                        title="Détection d’injection"
                        caption="Classe la requête : toute manipulation est bloquée avant le fournisseur."
                        :icon="ShieldCheck"
                        :status="run.stages.injection"
                    >
                        <template v-if="run.stages.injection === 'blocked'">
                            <p class="gov__line gov__line--danger">
                                Tentative de manipulation détectée — requête jamais transmise.
                            </p>
                            <div class="gauge">
                                <div
                                    class="gauge__fill gauge__fill--danger"
                                    :style="{ width: injectionScorePct + '%' }"
                                ></div>
                            </div>
                            <span class="muted">score {{ injectionScorePct }} %</span>
                        </template>
                        <template v-else>
                            <p class="gov__line gov__line--ok">Requête saine — aucune injection.</p>
                            <div class="gauge">
                                <div
                                    class="gauge__fill gauge__fill--ok"
                                    :style="{ width: injectionScorePct + '%' }"
                                ></div>
                            </div>
                            <span class="muted">score {{ injectionScorePct }} %</span>
                        </template>
                    </PipelineStage>

                    <PipelineStage
                        :index="3"
                        title="Acheminement"
                        caption="Route vers le bon modèle selon le tier, bascule en cas de panne."
                        :icon="Route"
                        :status="run.stages.routing"
                    >
                        <div v-if="routing" class="kv">
                            <div class="kv__row">
                                <span class="kv__k">Provider</span>
                                <span class="kv__v">{{ routing.provider || '—' }}</span>
                            </div>
                            <div class="kv__row">
                                <span class="kv__k">Modèle</span>
                                <span class="kv__v">{{ routing.model || '—' }}</span>
                            </div>
                            <div class="kv__row">
                                <span class="kv__k">Tier</span>
                                <span class="kv__v">{{ routing.tier || tier }}</span>
                            </div>
                            <div class="kv__row">
                                <span class="kv__k">Fallback</span>
                                <span class="kv__v">
                                    <PBadge
                                        v-if="routing.fallbackTriggered === 'true'"
                                        variant="warning"
                                        size="sm"
                                    >
                                        déclenché
                                    </PBadge>
                                    <template v-else>non</template>
                                </span>
                            </div>
                            <div v-if="routing.fallbackReason" class="kv__row">
                                <span class="kv__k">Raison</span>
                                <span class="kv__v kv__v--muted">{{ routing.fallbackReason }}</span>
                            </div>
                        </div>
                    </PipelineStage>

                    <PipelineStage
                        :index="4"
                        title="Coût & performance"
                        caption="Tokens, latence, cache et quota — mesurés sur l’appel réel."
                        :icon="Gauge"
                        :status="run.stages.cost"
                        last
                    >
                        <div v-if="routing" class="kv">
                            <div class="kv__row">
                                <span class="kv__k">Tokens</span>
                                <span class="kv__v">
                                    {{ routing.tokensInput || '?' }} → {{ routing.tokensOutput || '?' }}
                                </span>
                            </div>
                            <div class="kv__row">
                                <span class="kv__k">Latence</span>
                                <span class="kv__v">{{ routing.latencyMs ? routing.latencyMs + ' ms' : '—' }}</span>
                            </div>
                            <div v-if="cacheHitPct(routing) !== null" class="kv__row">
                                <span class="kv__k">Cache hit</span>
                                <span class="kv__v">{{ cacheHitPct(routing) }} %</span>
                            </div>
                            <div v-if="routing.quotaState" class="kv__row">
                                <span class="kv__k">Quota</span>
                                <span class="kv__v">
                                    <PBadge
                                        :variant="routing.quotaState === 'ok' ? 'success' : 'warning'"
                                        size="sm"
                                    >
                                        {{ routing.quotaState }}
                                    </PBadge>
                                </span>
                            </div>
                        </div>
                    </PipelineStage>
                </div>

                <p v-if="!hasRun" class="control__hint">
                    Envoyez un message pour voir PreLLM travailler, étape par étape.
                </p>
            </aside>
        </main>

        <ScenarioDialog
            v-model:open="dialogOpen"
            :kind="dialogKind"
            :families="catalogFor(dialogKind)"
            @select="onSelectPrompt"
        />
        <PToast />
    </div>
</template>

<style scoped>
.boot {
    min-height: 100vh;
    background: var(--p-color-bg);
}

.demo {
    --demo-max: 1320px;
    --demo-gutter: 28px;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--p-color-bg);
}

/* === Topbar === */
.demo__topbar {
    border-bottom: 1px solid var(--p-color-line);
    background: var(--p-color-surface);
}
.demo__bar {
    width: 100%;
    max-width: var(--demo-max);
    margin: 0 auto;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    padding: 16px var(--demo-gutter);
}
.demo__brand {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.demo__logo {
    font-family: var(--p-font-logo, 'Changa One', cursive);
    font-style: italic;
    font-size: var(--p-text-2xl);
    color: var(--p-color-ink);
    line-height: 1;
}
.demo__tagline {
    font-size: var(--p-text-xs);
    color: var(--p-color-ink-4);
}
.demo__host {
    font-family: var(--p-font-mono);
    color: var(--p-color-ink-5);
}
.demo__config {
    display: flex;
    align-items: flex-end;
    gap: 14px;
    flex-wrap: wrap;
}
.demo__config :deep(.p-input) {
    min-width: 240px;
}
.demo__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.demo__field-label {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    font-weight: var(--p-font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
}
.demo__select {
    min-width: 104px;
}
.demo__user {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 2px;
}
.demo__email {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    color: var(--p-color-ink-5);
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.demo__logout {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--p-radius-sm);
    border: 1px solid var(--p-color-line);
    background: var(--p-color-surface);
    color: var(--p-color-ink-4);
    cursor: pointer;
    transition:
        border-color var(--p-motion-fast) var(--p-ease-out),
        color var(--p-motion-fast) var(--p-ease-out);
}
.demo__logout:hover {
    border-color: var(--p-color-brick-soft);
    color: var(--p-color-brick);
}

/* === Main grid === */
.demo__main {
    flex: 1;
    width: 100%;
    max-width: var(--demo-max);
    margin: 0 auto;
    padding: 24px var(--demo-gutter) 32px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 28px;
    min-height: 0;
}
@media (max-width: 980px) {
    .demo__main {
        grid-template-columns: 1fr;
    }
    .chat__messages {
        max-height: none;
    }
}
@media (max-width: 640px) {
    .demo {
        --demo-gutter: 16px;
    }
    .demo__bar {
        gap: 16px;
    }
    .demo__config {
        width: 100%;
        gap: 12px;
    }
    .demo__config :deep(.p-input) {
        flex: 1 1 100%;
        min-width: 0;
    }
}

/* === Eyebrow partagé === */
.chat__eyebrow {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
}

/* === Chat === */
.chat {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-height: 0;
}
.chat__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
}
.chat__host {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    color: var(--p-color-ink-5);
}
.chat__messages {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 4px;
    min-height: 300px;
    max-height: calc(100vh - 300px);
}
.chat__empty {
    margin: auto;
    text-align: center;
    max-width: 42ch;
    color: var(--p-color-ink-4);
}
.chat__empty-title {
    font-family: var(--p-font-display);
    font-size: var(--p-text-lg);
    color: var(--p-color-ink);
    line-height: var(--p-line-snug);
    margin: 0 0 8px;
}
.chat__empty-sub {
    margin: 0;
    font-size: var(--p-text-sm);
    line-height: var(--p-line-relaxed);
}

.bubble {
    max-width: 88%;
    padding: 10px 14px;
    border-radius: var(--p-radius-md);
    border: 1px solid var(--p-color-line);
    background: var(--p-color-surface);
    display: flex;
    flex-direction: column;
    gap: 4px;
    animation: stage-reveal var(--p-motion-base) var(--p-ease-out);
}
@keyframes stage-reveal {
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: none;
    }
}
.bubble--user {
    align-self: flex-end;
    background: var(--p-color-ink);
    border-color: var(--p-color-ink);
}
.bubble--user .bubble__who {
    color: var(--p-color-ink-6);
}
.bubble--user .bubble__text {
    color: var(--p-color-surface);
}
.bubble--prellm {
    align-self: flex-start;
}
.bubble--blocked {
    background: var(--p-color-brick-bg);
    border-color: var(--p-color-brick-soft);
}
.bubble--error {
    background: var(--p-color-amber-bg);
    border-color: var(--p-color-amber-soft);
}
.bubble__who {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-5);
}
.bubble__text {
    margin: 0;
    font-size: var(--p-text-sm);
    line-height: var(--p-line-relaxed);
    color: var(--p-color-ink);
    white-space: pre-wrap;
    word-break: break-word;
}
.bubble__meta {
    margin: 2px 0 0;
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    color: var(--p-color-ink-5);
}

/* === Actions (2 boutons → dialog) === */
.actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}
.action {
    flex: 1 1 0;
    min-width: 180px;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    column-gap: 10px;
    row-gap: 1px;
    align-items: center;
    padding: 12px 16px;
    border-radius: var(--p-radius-sm);
    border: 1px solid var(--p-color-line);
    background: var(--p-color-surface);
    cursor: pointer;
    text-align: left;
    transition:
        border-color var(--p-motion-fast) var(--p-ease-out),
        background var(--p-motion-fast) var(--p-ease-out);
}
.action :deep(svg) {
    grid-row: 1 / 3;
    color: var(--p-color-ink-4);
    transition: color var(--p-motion-fast) var(--p-ease-out);
}
.action__label {
    font-size: var(--p-text-sm);
    font-weight: var(--p-font-weight-medium);
    color: var(--p-color-ink);
}
.action__sub {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    color: var(--p-color-ink-5);
}
.action:hover {
    background: var(--p-color-surface-alt);
}
.action--secrets:hover {
    border-color: var(--p-color-olive-soft);
}
.action--secrets:hover :deep(svg) {
    color: var(--p-color-olive);
}
.action--injection:hover {
    border-color: var(--p-color-brick-soft);
}
.action--injection:hover :deep(svg) {
    color: var(--p-color-brick);
}

/* === Composer === */
.composer {
    display: flex;
    gap: 12px;
    align-items: flex-end;
}
.composer__input {
    flex: 1;
    resize: vertical;
    background: var(--p-color-bg);
    color: var(--p-color-ink);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    padding: 10px 12px;
    font-family: var(--p-font-sans);
    font-size: var(--p-text-sm);
    line-height: var(--p-line-relaxed);
    transition: border-color var(--p-motion-fast) var(--p-ease-out);
}
.composer__input::placeholder {
    color: var(--p-color-ink-5);
}
.composer__input:focus-visible {
    outline: none;
    border-color: var(--p-color-ink-3);
}

/* === Control / pipeline === */
.control {
    display: flex;
    flex-direction: column;
    gap: 18px;
    border-left: 1px solid var(--p-color-line-soft);
    padding-left: 28px;
}
@media (max-width: 980px) {
    .control {
        border-left: none;
        padding-left: 0;
        border-top: 1px solid var(--p-color-line-soft);
        padding-top: 20px;
    }
}
.control__head {
    display: flex;
    align-items: center;
    gap: 10px;
}
.pipeline {
    display: flex;
    flex-direction: column;
}
.control__hint {
    margin: 0;
    font-size: var(--p-text-xs);
    color: var(--p-color-ink-5);
    line-height: var(--p-line-normal);
}

/* === Contenu d'étapes === */
.muted {
    font-size: var(--p-text-xs);
    color: var(--p-color-ink-5);
    line-height: var(--p-line-normal);
}
.gov__line {
    margin: 0;
    font-size: var(--p-text-sm);
    color: var(--p-color-ink);
    line-height: var(--p-line-normal);
}
.gov__line--ok {
    color: var(--p-color-euca);
}
.gov__line--danger {
    color: var(--p-color-brick);
}

.diff {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.diff__col {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.diff__label {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
}
.diff__text {
    padding: 10px 12px;
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    background: var(--p-color-bg);
    font-size: var(--p-text-sm);
    line-height: var(--p-line-relaxed);
    color: var(--p-color-ink);
}
.detections {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.detection {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.detection__orig {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-xs);
    color: var(--p-color-brick);
    word-break: break-all;
}
.detection__arrow {
    color: var(--p-color-ink-5);
}
.detection__token {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-xs);
    color: var(--p-color-olive);
}

.gauge {
    height: 6px;
    border-radius: var(--p-radius-full);
    background: var(--p-color-surface-alt);
    overflow: hidden;
}
.gauge__fill {
    height: 100%;
    border-radius: var(--p-radius-full);
    transition: width var(--p-motion-base) var(--p-ease-out);
}
.gauge__fill--ok {
    background: var(--p-color-euca);
}
.gauge__fill--danger {
    background: var(--p-color-brick);
}

.kv {
    display: flex;
    flex-direction: column;
}
.kv__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 7px 0;
    border-bottom: 1px solid var(--p-color-line-soft);
}
.kv__row:last-child {
    border-bottom: none;
}
.kv__k {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
}
.kv__v {
    font-size: var(--p-text-sm);
    color: var(--p-color-ink);
    text-align: right;
    word-break: break-word;
}
.kv__v--muted {
    color: var(--p-color-ink-4);
    font-size: var(--p-text-xs);
}
</style>
